<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
      <!-- Header Section -->
      <div class="mb-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Reports & Analytics</h1>
            <p class="text-base text-gray-600 dark:text-gray-400 mt-2">Comprehensive insights and business analytics</p>
          </div>
          <div class="flex items-center gap-3">
            <select v-model="selectedPeriod" class="form-input text-sm">
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last Year</option>
            </select>
            <button class="btn btn-primary text-sm px-4 py-2" @click="exportReport">Export Report</button>
          </div>
        </div>
      </div>

      <!-- Key Performance Indicators -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Revenue Growth</p>
                <p class="text-3xl font-bold text-green-600 mb-1">+{{ revenueGrowth }}%</p>
                <p class="text-xs text-gray-500 dark:text-gray-500">vs previous period</p>
              </div>
              <div class="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Member Retention</p>
                <p class="text-3xl font-bold text-blue-600 mb-1">{{ memberRetention }}%</p>
                <p class="text-xs text-gray-500 dark:text-gray-500">active members</p>
              </div>
              <div class="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Class Attendance</p>
                <p class="text-3xl font-bold text-purple-600 mb-1">{{ classAttendance }}%</p>
                <p class="text-xs text-gray-500 dark:text-gray-500">average capacity</p>
              </div>
              <div class="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-body p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Equipment Utilization</p>
                <p class="text-3xl font-bold text-orange-600 mb-1">{{ equipmentUtilization }}%</p>
                <p class="text-xs text-gray-500 dark:text-gray-500">in use rate</p>
              </div>
              <div class="w-14 h-14 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                <svg class="w-7 h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section - Revenue Trends and User Distribution -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <!-- Revenue Chart -->
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-header dark:border-gray-800 py-4">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Revenue Trends</h2>
          </div>
          <div class="card-body p-6">
            <div class="h-64 flex items-end justify-center space-x-3">
              <div v-for="(value, index) in revenueData" :key="index" class="flex flex-col items-center">
                <div class="text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">{{ value.label }}</div>
                <div class="w-10 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t shadow-sm" :style="{ height: (value.value / maxRevenue * 200) + 'px' }"></div>
                <div class="text-sm font-semibold mt-2 text-gray-900 dark:text-gray-100">ETB {{ value.value.toLocaleString() }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- User Distribution Pie Chart -->
        <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
          <div class="card-header dark:border-gray-800 py-4">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">User Distribution</h2>
          </div>
          <div class="card-body p-6">
                         <div class="h-64 flex items-center justify-center">
               <div class="relative w-48 h-48">
                 <!-- Pie Chart -->
                 <svg class="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                       <!-- Active Users (Green) -->
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke-width="20"
                      stroke="#10B981"
                      :stroke-dasharray="`${(userDistribution.active / userDistribution.total) * 251.2} 251.2`"
                      stroke-dashoffset="0"
                    />
                    
                    <!-- New Users (Blue) -->
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke-width="20"
                      stroke="#3B82F6"
                      :stroke-dasharray="`${(userDistribution.new / userDistribution.total) * 251.2} 251.2`"
                      :stroke-dashoffset="`-${(userDistribution.active / userDistribution.total) * 251.2}`"
                    />
                   
                   <!-- Expiring Users (Yellow) -->
                   <circle
                     cx="50"
                     cy="50"
                     r="40"
                     fill="none"
                     stroke-width="20"
                     stroke="#F59E0B"
                     :stroke-dasharray="`${(userDistribution.expiring / userDistribution.total) * 251.2} 251.2`"
                     :stroke-dashoffset="`-${((userDistribution.active + userDistribution.new) / userDistribution.total) * 251.2}`"
                   />
                   
                   <!-- Inactive Users (Red) -->
                   <circle
                     cx="50"
                     cy="50"
                     r="40"
                     fill="none"
                     stroke-width="20"
                     stroke="#EF4444"
                     :stroke-dasharray="`${(userDistribution.inactive / userDistribution.total) * 251.2} 251.2`"
                     :stroke-dashoffset="`-${((userDistribution.active + userDistribution.new + userDistribution.expiring) / userDistribution.total) * 251.2}`"
                   />
                 </svg>
                 
                 <!-- Center Text -->
                 <div class="absolute inset-0 flex items-center justify-center">
                   <div class="text-center">
                     <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ userDistribution.total }}</div>
                     <div class="text-sm text-gray-500 dark:text-gray-400">Total Users</div>
                   </div>
                 </div>
               </div>
             </div>
            
                                      <!-- Legend -->
             <div class="mt-6 grid grid-cols-2 gap-3 text-sm">
               <div class="flex items-center">
                 <div class="w-4 h-4 bg-green-500 rounded-full mr-3 shadow-sm"></div>
                 <span class="font-medium text-gray-700 dark:text-gray-300">Active ({{ userDistribution.active.toLocaleString() }})</span>
               </div>
               <div class="flex items-center">
                 <div class="w-4 h-4 bg-blue-500 rounded-full mr-3 shadow-sm"></div>
                 <span class="font-medium text-gray-700 dark:text-gray-300">New ({{ userDistribution.new.toLocaleString() }})</span>
               </div>
               <div class="flex items-center">
                 <div class="w-4 h-4 bg-yellow-500 rounded-full mr-3 shadow-sm"></div>
                 <span class="font-medium text-gray-700 dark:text-gray-300">Expiring ({{ userDistribution.expiring.toLocaleString() }})</span>
               </div>
               <div class="flex items-center">
                 <div class="w-4 h-4 bg-red-500 rounded-full mr-3 shadow-sm"></div>
                 <span class="font-medium text-gray-700 dark:text-gray-300">Inactive ({{ userDistribution.inactive.toLocaleString() }})</span>
               </div>
             </div>
          </div>
        </div>
      </div>

             <!-- Member Demographics -->
       <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
         <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
           <div class="card-header dark:border-gray-800 py-4">
             <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Member Demographics</h2>
           </div>
           <div class="card-body p-6">
             <div class="space-y-5">
               <div v-for="demo in memberDemographics" :key="demo.category" class="flex items-center justify-between">
                 <div class="flex items-center">
                   <div class="w-5 h-5 rounded-full mr-4 shadow-sm" :class="demo.color"></div>
                   <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ demo.category }}</span>
                 </div>
                 <div class="flex items-center">
                   <div class="w-28 bg-gray-200 dark:bg-gray-700 rounded-full h-3 mr-4">
                     <div class="h-3 rounded-full shadow-sm" :class="demo.color" :style="{ width: demo.percentage + '%' }"></div>
                   </div>
                   <span class="text-sm font-semibold text-gray-900 dark:text-gray-100 w-12 text-right">{{ demo.percentage }}%</span>
                 </div>
               </div>
             </div>
           </div>
         </div>

         <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
           <div class="card-header dark:border-gray-800 py-4">
             <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Popular Classes</h2>
           </div>
           <div class="card-body p-6">
             <div class="space-y-5">
               <div v-for="cls in popularClasses" :key="cls.name" class="flex items-center justify-between">
                 <div class="flex items-center">
                   <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-4">
                     <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                     </svg>
                   </div>
                   <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ cls.name }}</span>
                 </div>
                 <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ cls.enrollment.toLocaleString() }} members</div>
               </div>
             </div>
           </div>
         </div>
       </div>

             <!-- Detailed Reports -->
       <div class="card dark:bg-gray-900 mb-8 hover:shadow-lg transition-all duration-200">
         <div class="card-header dark:border-gray-800 py-4">
           <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Detailed Reports</h2>
         </div>
         <div class="card-body p-6">
           <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
             <div class="p-5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200">
               <h3 class="font-semibold mb-4 text-gray-900 dark:text-gray-100">Financial Summary</h3>
               <div class="space-y-3 text-sm">
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Total Revenue:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">ETB {{ totalRevenue.toLocaleString(2) }}</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Membership Fees:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">ETB {{ membershipFees.toLocaleString(2) }}</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Class Revenue:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">ETB {{ classRevenue.toLocaleString(2) }}</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Other Services:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">ETB {{ otherRevenue.toLocaleString(2) }}</span>
                 </div>
               </div>
             </div>

             <div class="p-5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200">
               <h3 class="font-semibold mb-4 text-gray-900 dark:text-gray-100">Member Statistics</h3>
               <div class="space-y-3 text-sm">
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Total Members:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">{{ totalMembers.toLocaleString() }}</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Active Members:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">{{ activeMembers.toLocaleString() }}</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">New This Month:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">{{ newMembers.toLocaleString() }}</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Churn Rate:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">{{ churnRate }}%</span>
                 </div>
               </div>
             </div>

             <div class="p-5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200">
               <h3 class="font-semibold mb-4 text-gray-900 dark:text-gray-100">Operational Metrics</h3>
               <div class="space-y-3 text-sm">
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Avg Daily Attendance:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">{{ avgDailyAttendance.toLocaleString() }}</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Peak Hours:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">{{ peakHours }}</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Equipment Downtime:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">{{ equipmentDowntime }}%</span>
                 </div>
                 <div class="flex justify-between items-center">
                   <span class="text-gray-600 dark:text-gray-400">Staff Utilization:</span>
                   <span class="font-semibold text-gray-900 dark:text-gray-100">{{ staffUtilization }}%</span>
                 </div>
               </div>
             </div>
           </div>
         </div>
       </div>

             <!-- Export Options -->
       <div class="card dark:bg-gray-900 hover:shadow-lg transition-all duration-200">
         <div class="card-header dark:border-gray-800 py-4">
           <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Export & Generate Reports</h2>
         </div>
         <div class="card-body p-6">
           <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
             <button class="btn btn-outline hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 h-14 group" @click="generateReport('financial')">
               <svg class="w-5 h-5 mr-3 text-blue-600 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
               </svg>
               <span class="font-medium text-blue-600">Financial Report</span>
             </button>
             
             <button class="btn btn-outline hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200 h-14 group" @click="generateReport('membership')">
               <svg class="w-5 h-5 mr-3 text-green-600 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
               </svg>
               <span class="font-medium text-green-600">Membership Report</span>
             </button>
             
             <button class="btn btn-outline hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200 h-14 group" @click="generateReport('attendance')">
               <svg class="w-5 h-5 mr-3 text-purple-600 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
               </svg>
               <span class="font-medium text-purple-600">Attendance Report</span>
             </button>
             
             <button class="btn btn-outline hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200 h-14 group" @click="generateReport('equipment')">
               <svg class="w-5 h-5 mr-3 text-orange-600 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
               </svg>
               <span class="font-medium text-orange-600">Equipment Report</span>
             </button>
           </div>
         </div>
       </div>
    </div>
  </div>
</template>

<script>
export default {
  data: () => ({
    selectedPeriod: '30',
    revenueData: [
      { label: 'Week 1', value: 12500 },
      { label: 'Week 2', value: 15800 },
      { label: 'Week 3', value: 14200 },
      { label: 'Week 4', value: 18900 }
    ],
    memberDemographics: [
      { category: '18-25', percentage: 25, color: 'bg-blue-500' },
      { category: '26-35', percentage: 35, color: 'bg-green-500' },
      { category: '36-45', percentage: 22, color: 'bg-yellow-500' },
      { category: '46+', percentage: 18, color: 'bg-red-500' }
    ],
    popularClasses: [
      { name: 'Yoga Flow', enrollment: 45 },
      { name: 'HIIT Cardio', enrollment: 38 },
      { name: 'Strength Training', enrollment: 32 },
      { name: 'Spinning', enrollment: 28 }
    ],
    userDistribution: {
      active: 1087,
      new: 45,
      expiring: 118,
      inactive: 163,
      total: 1250
    }
  }),
  computed: {
    maxRevenue() {
      return Math.max(...this.revenueData.map(d => d.value));
    },
    revenueGrowth() {
      return 12.5; // Mock data
    },
    memberRetention() {
      return 87.3; // Mock data
    },
    classAttendance() {
      return 78.9; // Mock data
    },
    equipmentUtilization() {
      return 65.2; // Mock data
    },
    totalRevenue() {
      return this.revenueData.reduce((sum, d) => sum + d.value, 0);
    },
    membershipFees() {
      return this.totalRevenue * 0.65;
    },
    classRevenue() {
      return this.totalRevenue * 0.25;
    },
    otherRevenue() {
      return this.totalRevenue * 0.10;
    },
    totalMembers() {
      return 1250; // Mock data
    },
    activeMembers() {
      return 1087; // Mock data
    },
    newMembers() {
      return 45; // Mock data
    },
    churnRate() {
      return 3.2; // Mock data
    },
    avgDailyAttendance() {
      return 187; // Mock data
    },
    peakHours() {
      return '6-8 AM, 5-7 PM'; // Mock data
    },
    equipmentDowntime() {
      return 8.5; // Mock data
    },
    staffUtilization() {
      return 92.1; // Mock data
    }
  },
  methods: {
    exportReport() {
      this.$bus.$emit('toast:show', { title: 'Report exported successfully', type: 'success' });
    },
    generateReport(type) {
      this.$bus.$emit('toast:show', { title: `${type.charAt(0).toUpperCase() + type.slice(1)} report generated`, type: 'success' });
    }
  }
}
</script>
